/**
 * Shared SEO landing page template — modern, Framer-style layout.
 * Inspired by the /developers page: gradient hero, rounded containers, clean visual hierarchy.
 * All 4 SEO pages (/ai-teammates, /marketing, /google-ads, /seo) render through this template.
 */

import { Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Fragment, useEffect, useRef } from "react";
import { PublicLayout } from "@/components/public-layout";
import {
	SeoFaqSection,
	SeoLongFormContent,
	type SeoRenderableFaqItem,
	type SeoRenderableSection,
} from "@/components/seo/seo-content-sections";
import { Badge } from "@/components/ui/badge";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getLogoUrl, INTEGRATIONS } from "@/constants/integration-logos";
import type {
	SeoLandingInternalRoute,
	SeoLandingSectionId,
	SeoLandingUiConfig,
} from "@/constants/seo/seo-landing-ui";
import { usePostHog } from "@/integrations/posthog/provider";
import { cn, formatSentenceCaseTitle } from "@/lib/utils";

const NEW_TAB_REL = "noopener noreferrer";

interface SeoLandingInternalLink {
	to: SeoLandingInternalRoute;
	label: string;
	description: string;
}

interface SeoLandingExternalLink {
	href: string;
	label: string;
	description: string;
}

interface SeoLandingSpotlight {
	title: string;
	description: string;
	items: string[];
}

interface SeoLandingTemplateProps {
	ui: SeoLandingUiConfig;
	h1: string;
	introParagraphs: string[];
	outline: string[];
	sections: SeoRenderableSection[];
	internalLinks: SeoLandingInternalLink[];
	externalLinks: SeoLandingExternalLink[];
	faqItems: SeoRenderableFaqItem[];
	spotlight?: SeoLandingSpotlight;
}

const TRACKED_SECTION_IDS: SeoLandingSectionId[] = [
	"value-props",
	"onboarding",
	"channel-module",
	"real-examples",
	"deep-dive",
	"backlinks",
	"faq",
	"final-cta",
];

export function SeoLandingTemplate({
	ui,
	h1,
	introParagraphs: _introParagraphs,
	outline: _outline,
	sections,
	internalLinks,
	externalLinks,
	faqItems,
	spotlight,
}: SeoLandingTemplateProps) {
	const posthog = usePostHog();
	const seenSectionViewsRef = useRef(new Set<string>());
	const seenTopicExpandRef = useRef(new Set<string>());

	const uniqueInternalLinks = dedupeInternalLinks(internalLinks);
	const pagePath = typeof window !== "undefined" ? window.location.pathname || "/" : "/";

	const captureCtaClick = (ctaId: string, sectionId: string, position: string) => {
		posthog?.capture("seo_cta_click", {
			page_path: pagePath,
			cta_id: ctaId,
			section_id: sectionId,
			position,
		});
	};

	const captureTopicVisible = (topicId: string) => {
		if (!posthog || seenTopicExpandRef.current.has(topicId)) return;
		seenTopicExpandRef.current.add(topicId);
		posthog.capture("seo_deep_dive_expand", { page_path: pagePath, topic_id: topicId });
	};

	useEffect(() => {
		if (!posthog || typeof IntersectionObserver === "undefined") return;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) continue;
					const sectionId = (entry.target as HTMLElement).id;
					if (!sectionId || seenSectionViewsRef.current.has(sectionId)) continue;
					seenSectionViewsRef.current.add(sectionId);
					posthog.capture("seo_section_view", { page_path: pagePath, section_id: sectionId });
				}
			},
			{ threshold: 0.35, rootMargin: "0px 0px -20% 0px" }
		);

		for (const sectionId of ["hero", ...TRACKED_SECTION_IDS]) {
			const node = document.getElementById(sectionId);
			if (node) observer.observe(node);
		}

		return () => observer.disconnect();
	}, [posthog, pagePath]);

	const renderContextualCtas = (afterSectionId: SeoLandingSectionId) => {
		const ctas = ui.contextualCtas.filter((cta) => cta.afterSectionId === afterSectionId);
		if (ctas.length === 0) return null;

		return (
			<div className="text-center" data-testid={`seo-contextual-cta-group-${afterSectionId}`}>
				{ctas.map((cta, index) => (
					<div
						key={cta.id}
						data-testid={`seo-contextual-cta-${cta.id}`}
						className={cn(index > 0 && "mt-6")}
					>
						<p className="text-muted-foreground">{cta.supportingText}</p>
						<Button
							className="mt-3"
							variant={cta.variant ?? "default"}
							asChild
							onClick={() => captureCtaClick(cta.id, afterSectionId, `contextual-${index + 1}`)}
						>
							<Link to={cta.to}>{cta.label}</Link>
						</Button>
					</div>
				))}
			</div>
		);
	};

	const si = (sectionId: SeoLandingSectionId, fallbackTitle: string, fallbackBody: string) => {
		const configured = ui.sectionIntros.find((s) => s.sectionId === sectionId);
		return { title: configured?.title ?? fallbackTitle, body: configured?.body ?? fallbackBody };
	};

	const valueIntro = si(
		"value-props",
		"Why it works",
		"Not another dashboard. An actual operator."
	);
	const onboardingIntro = si(
		"onboarding",
		"How it works",
		"Three steps. One teammate. Real output."
	);
	const moduleIntro = si("channel-module", ui.pageModule.title, ui.pageModule.body);
	const useCaseIntro = si("real-examples", "Real use cases", "Copy one. Adapt it. Ship today.");
	const deepDiveIntro = si("deep-dive", ui.deepDiveTitle, ui.deepDiveIntro);
	const faqIntro = si("faq", "FAQ", "Quick answers to common questions.");
	const finalIntro = si("final-cta", ui.finalCtaTitle, ui.finalCtaBody);

	return (
		<PublicLayout>
			<article
				aria-label={ui.articleLabel}
				className="mx-auto w-full max-w-6xl space-y-16 px-4 text-foreground sm:space-y-20 sm:px-6 lg:px-8"
			>
				{/* ── Breadcrumbs ── */}
				{ui.breadcrumbs.length > 0 && (
					<div className="pt-6" data-testid="seo-breadcrumb">
						<Breadcrumb>
							<BreadcrumbList>
								{ui.breadcrumbs.map((crumb, index) => {
									const isLast = index === ui.breadcrumbs.length - 1;
									return (
										<Fragment key={crumb.to}>
											{index > 0 && <BreadcrumbSeparator />}
											<BreadcrumbItem>
												{isLast ? (
													<BreadcrumbPage>{crumb.label}</BreadcrumbPage>
												) : (
													<BreadcrumbLink asChild>
														<Link to={crumb.to}>{crumb.label}</Link>
													</BreadcrumbLink>
												)}
											</BreadcrumbItem>
										</Fragment>
									);
								})}
							</BreadcrumbList>
						</Breadcrumb>
					</div>
				)}

				{/* ── Hero (gradient container like /developers) ── */}
				<section
					id="hero"
					data-testid="seo-hero-section"
					className="relative isolate overflow-hidden rounded-3xl border bg-card/90 px-6 py-16 text-center shadow-lg sm:py-20"
				>
					<div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-primary/15 via-transparent to-secondary/20" />
					<Badge variant="secondary" className="mx-auto w-fit text-xs uppercase tracking-wide">
						{formatSentenceCaseTitle(ui.hero.kicker)}
					</Badge>
					<h1 className="mx-auto mt-6 max-w-4xl text-balance text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
						{formatSentenceCaseTitle(h1)}
					</h1>
					<p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">{ui.hero.subhead}</p>
					<div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
						<Button
							size="lg"
							asChild
							onClick={() => captureCtaClick("hero-primary", "hero", "hero-primary")}
						>
							<Link to={ui.hero.primaryCta.to}>
								{ui.hero.primaryCta.label} <ArrowRight className="size-4" />
							</Link>
						</Button>
						<Button
							variant="ghost"
							size="lg"
							asChild
							onClick={() => captureCtaClick("hero-secondary", "hero", "hero-secondary")}
						>
							<Link to={ui.hero.secondaryCta.to}>{ui.hero.secondaryCta.label}</Link>
						</Button>
					</div>
					<div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
						{ui.hero.proofPoints.slice(0, 3).map((point, i) => (
							<Fragment key={point}>
								{i > 0 && <span className="text-xl text-muted-foreground">·</span>}
								<span>{point}</span>
							</Fragment>
						))}
					</div>
				</section>

				{/* ── Integration logos ── */}
				{ui.integrationLogos && ui.integrationLogos.length > 0 && (
					<section className="py-8 sm:py-10" data-testid="seo-integration-logos">
						<p className="mb-5 text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground">
							Works with
						</p>
						<div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
							{ui.integrationLogos.map((id) => {
								const ref = INTEGRATIONS[id];
								if (!ref) return null;
								return (
									<div key={id} className="flex flex-col items-center gap-1.5">
										<img
											src={getLogoUrl(ref.logoKey)}
											alt={ref.name}
											className="h-8 w-8 object-contain opacity-60 transition-opacity hover:opacity-100"
										/>
										<span className="text-[11px] text-muted-foreground">{ref.name}</span>
									</div>
								);
							})}
						</div>
					</section>
				)}

				{/* ── Value props (card grid like /developers features) ── */}
				<section id="value-props" data-testid="seo-value-grid" className="space-y-6">
					<SectionHeading title={valueIntro.title} subtitle={valueIntro.body} />
					<div className="grid gap-4 md:grid-cols-3">
						{ui.valueCards.map((card) => (
							<Card key={card.title} className="h-full">
								<CardHeader className="space-y-2">
									<CardTitle className="text-lg">{formatSentenceCaseTitle(card.title)}</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-sm text-muted-foreground">{card.description}</p>
								</CardContent>
							</Card>
						))}
					</div>
				</section>

				{renderContextualCtas("value-props")}

				{/* ── How it works (numbered steps) ── */}
				<section id="onboarding" data-testid="seo-onboarding-section" className="space-y-6">
					<SectionHeading title={onboardingIntro.title} subtitle={onboardingIntro.body} />
					<div className="grid gap-6 rounded-3xl border bg-muted/30 p-8 md:grid-cols-2">
						{ui.steps.map((step, index) => (
							<div key={step.title} className="flex gap-4">
								<span className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background text-sm font-bold">
									{index + 1}
								</span>
								<div>
									<p className="font-semibold text-foreground">
										{formatSentenceCaseTitle(step.title)}
									</p>
									<p className="mt-1 text-sm text-muted-foreground">{step.description}</p>
								</div>
							</div>
						))}
					</div>
				</section>

				{/* ── Channel module (card grid) ── */}
				<section id="channel-module" data-testid="seo-page-module-section" className="space-y-6">
					<SectionHeading title={moduleIntro.title} subtitle={moduleIntro.body} />
					<div className="grid gap-4 md:grid-cols-3">
						{ui.pageModule.cards.map((card) => (
							<Card key={card.title} className="h-full">
								<CardHeader>
									<CardTitle className="text-lg">{formatSentenceCaseTitle(card.title)}</CardTitle>
								</CardHeader>
								<CardContent className="space-y-3">
									<p className="text-sm text-muted-foreground">{card.body}</p>
									<ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
										{card.bullets.map((b) => (
											<li key={b}>{b}</li>
										))}
									</ul>
								</CardContent>
							</Card>
						))}
					</div>
				</section>

				{/* ── Use cases ── */}
				<section id="real-examples" data-testid="seo-use-cases-section" className="space-y-6">
					<SectionHeading title={useCaseIntro.title} subtitle={useCaseIntro.body} />
					<div className="grid gap-4 md:grid-cols-1">
						{ui.useCases.map((uc) => (
							<Card key={uc.title}>
								<CardHeader>
									<CardTitle className="text-lg">{formatSentenceCaseTitle(uc.title)}</CardTitle>
								</CardHeader>
								<CardContent className="space-y-2 text-sm text-muted-foreground">
									<p>
										<span className="font-semibold text-foreground">Scenario:</span> {uc.scenario}
									</p>
									<p>
										<span className="font-semibold text-foreground">Task:</span> {uc.exampleTask}
									</p>
									<p>
										<span className="font-semibold text-foreground">Result:</span> {uc.outcome}
									</p>
								</CardContent>
							</Card>
						))}
					</div>
				</section>

				{renderContextualCtas("real-examples")}

				{/* ── Long-form deep dive ── */}
				<section id="deep-dive" data-testid="seo-deep-dive-section" className="space-y-6">
					<SectionHeading title={deepDiveIntro.title} subtitle={deepDiveIntro.body} />
					<SeoLongFormContent
						sections={sections}
						onSectionVisible={captureTopicVisible}
						collapsedAfter={ui.deepDiveCollapsedAfter}
					/>
				</section>

				{renderContextualCtas("deep-dive")}

				{/* ── Explore more (only sibling SEO pages + external references) ── */}
				<section id="backlinks" className="grid gap-8 sm:grid-cols-2">
					{uniqueInternalLinks.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="text-lg">Explore more</CardTitle>
							</CardHeader>
							<CardContent>
								<ul className="space-y-3">
									{uniqueInternalLinks.map((link) => (
										<li key={link.to}>
											<Link
												to={link.to}
												className="group flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary"
											>
												{link.label}
												<ArrowRight className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
											</Link>
											<p className="text-xs text-muted-foreground">{link.description}</p>
										</li>
									))}
								</ul>
								{spotlight && (
									<p className="mt-4 text-xs text-muted-foreground">
										<span className="font-semibold text-foreground">{spotlight.title}:</span>{" "}
										{spotlight.items.slice(0, 8).join(", ")}.
									</p>
								)}
							</CardContent>
						</Card>
					)}
					{externalLinks.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="text-lg">References</CardTitle>
							</CardHeader>
							<CardContent>
								<ul className="space-y-3">
									{externalLinks.map((link) => (
										<li key={link.href}>
											<a
												href={link.href}
												target="_blank"
												rel={NEW_TAB_REL}
												className="group flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary"
											>
												{link.label}
												<ArrowUpRight className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
											</a>
											<p className="text-xs text-muted-foreground">{link.description}</p>
										</li>
									))}
								</ul>
							</CardContent>
						</Card>
					)}
				</section>

				{/* ── FAQ ── */}
				<SeoFaqSection items={faqItems} title={faqIntro.title} description={faqIntro.body} />

				{/* ── Final CTA (gradient like /developers) ── */}
				<section
					id="final-cta"
					data-testid="seo-final-cta"
					className="rounded-3xl border bg-gradient-to-br from-primary to-primary/90 px-6 py-12 text-center text-primary-foreground shadow-lg"
				>
					<h2 className="text-3xl font-semibold">{formatSentenceCaseTitle(finalIntro.title)}</h2>
					<p className="mx-auto mt-4 max-w-2xl text-base text-primary-foreground/90">
						{finalIntro.body}
					</p>
					<div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
						<Button
							size="lg"
							variant="secondary"
							asChild
							onClick={() => captureCtaClick("final-primary", "final-cta", "final-primary")}
						>
							<Link to={ui.finalPrimaryCta.to}>{ui.finalPrimaryCta.label}</Link>
						</Button>
						<Button
							size="lg"
							variant="ghost"
							className="text-primary-foreground"
							asChild
							onClick={() => captureCtaClick("final-secondary", "final-cta", "final-secondary")}
						>
							<Link to={ui.finalSecondaryCta.to} className="inline-flex items-center gap-1">
								{ui.finalSecondaryCta.label} <ArrowUpRight className="size-4" />
							</Link>
						</Button>
					</div>
				</section>
			</article>
		</PublicLayout>
	);
}

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
	return (
		<div className="text-center">
			<h2 className="text-3xl font-semibold tracking-tight">{formatSentenceCaseTitle(title)}</h2>
			{subtitle && (
				<p className="mx-auto mt-3 max-w-[60ch] text-base text-muted-foreground">{subtitle}</p>
			)}
		</div>
	);
}

function dedupeInternalLinks(links: SeoLandingInternalLink[]) {
	const seen = new Set<SeoLandingInternalRoute>();
	return links.filter((link) => {
		if (seen.has(link.to)) return false;
		seen.add(link.to);
		return true;
	});
}
